import { Injectable, EventEmitter } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { ReplaySubject } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MapService {

  map$ = new ReplaySubject<mapboxgl.Map>(1);
  layers$ = new ReplaySubject<LayerItem[]>(1);
  highlight$ = new EventEmitter<MapElement>();
  removeHighlight$ = new EventEmitter<MapElement>();
  styleChanged$ = new EventEmitter<StyleType>();

  private mapChange$ = new EventEmitter();

  constructor() {
    this.map$.subscribe(mapbox => {
      mapbox.on('sourcedata', (e) => {
        if (e.isSourceLoaded) {
          this.mapChange$.next();
        }
      });

      this.observe(mapbox);
    });
  }

  setStyle(style: StyleType): void {
    this.map$.subscribe(map => {
      map.setStyle(environment.mapbox.styles[style]);

      setTimeout(() => {
        this.styleChanged$.next(style);
      }, 1000);
    });
  }

  setLayerVisibility(id: string, visible: boolean) {
    const visibility = visible ? 'visible' : 'none';
    this.map$
      .pipe(take(1))
      .subscribe(m => m.setLayoutProperty(id, 'visibility', visibility));
  }

  private observe(mapbox: mapboxgl.Map) {
    this.mapChange$
      .pipe(debounceTime(2000))
      .subscribe(() => this.refresh(mapbox));
  }

  private refresh(mapbox: mapboxgl.Map) {
    const layers: LayerItem[] = [];
    mapbox.getStyle().layers.forEach(l => {
      if (!l.id.startsWith('mitra-')) {
        return;
      }
      const name = l.id.replace(/mitra-(result-)?/, '').toUpperCase();
      layers.push({
        id: l.id,
        name,
        count: l['source-layer'] ? l['source-layer'].length : 0,
        visible: l.layout ? l.layout.visibility === 'visible' : false
      });
    });
    this.layers$.next(layers);
  }

}

export interface LayerItem {
  id: string;
  name: string;
  count: number;
  visible: boolean;
}

export interface MapElement {
  id: string;
  lng: number;
  lat: number;
}

export type StyleType = 'satellite' | 'street';
